import SearchTracker from "@grindex-client/components/Search/SearchTracker.class";
import ProductsComparer from "@grindex-client/components/Search/ProductsComparer.class";
import SearchBaseDom from "./search-base.dom";
import { SearchResultParser } from "./helpers/SearchResultParser";
import { events } from "@grindex-common/helpers/events";
import { styleCheckBoxes } from "@grindex-common/helpers/styledCheckBox";
import { apiConfig } from "@grindex-common/helpers/apiConfig";
import { getSearchTabNumber, type SearchTab } from "./helpers/search-tab";

import { addListener, hideElement, showElement } from "@grindex-common/helpers/domManipulation";
import { chunk } from "@grindex-client/common/js/helpers/array-helpers";
import { underscoreTemplateGrindex } from "@grindex-client/common/js/helpers/underscore-helper";
import type { $TSFIXME } from "@grindex-client/common/js/types/typescript-migration";

const SELECTOR_RESULT_TEMPLATE = "#freetext-search-initial-result";

export type SearchResult = {
	total: number;
	items: $TSFIXME[];
	facets: string | $TSFIXME[];
	showDiscontinuedProducts: $TSFIXME;
};

export default class SearchBase {
	brandCategoryId: number | undefined;
	dirtyManager: $TSFIXME;
	eventManager: $TSFIXME;
	$module: HTMLElement;
	resultItemTemplate: $TSFIXME;
	dom: SearchBaseDom;
	queryObject: $TSFIXME;
	productsComparer: ProductsComparer;
	queryStringGlobal = "";
	currentPageId: string | undefined;
	searchTracker: SearchTracker;
	resultParser: SearchResultParser;
	isApi: $TSFIXME;
	initialRun = true;
	siblingTabs: $TSFIXME;
	tab: SearchTab | undefined;
	constructor($ownerModule: HTMLElement, eventManager, dirtyManager, queryObject) {
		this.brandCategoryId = window.config.search?.brandCategoryId;
		this.dirtyManager = dirtyManager;
		this.eventManager = eventManager;

		this.$module = $ownerModule;
		this.resultItemTemplate = underscoreTemplateGrindex(document.querySelector(SELECTOR_RESULT_TEMPLATE)?.innerHTML ?? "<div></div>");
		this.dom = new SearchBaseDom(this.$module);

		this.queryObject = queryObject;
		this.productsComparer = new ProductsComparer(this.$module);

		this.currentPageId = apiConfig.apiNode;
		this.searchTracker = new SearchTracker(this.parseQuery);
		this.resultParser = new SearchResultParser();
		this.queryObject.setQueryFromUrl();

		this.bindInitialEvents();

		this.toggleFilterPanel();

		this.hideHiddenElements();
	}

	private bindInitialEvents() {
		this.eventManager.on(this, events.search.QUERY_DATA_MODIFIED, this.updateClearAllBtn.bind(this));
		this.eventManager.on(this, events.search.SEARCH, this.search.bind(this));
		this.eventManager.on(this, events.search.PAGE_CHANGED, this.setPage.bind(this));
	}

	private hideHiddenElements() {
		this.dom.hideElementsHiddenOnResults();
	}

	search(renderSiblings: boolean = false) {
		if (this.isApi) {
			this.apiSearch(renderSiblings);
			return;
		}
		this.toggleLoader();
		const fullUrl = `/${apiConfig.apiLanguage}${window.config.search?.apiUrl}${apiConfig.apiNode}`;

		fetch(fullUrl, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(this.queryObject.queryData),
		})
			.then((response) => {
				if (response.ok) {
					response.json().then((result) => {
						this.setBrowserQuery();
						this.initializeSearchResults(result);
						this.searchTracker.bindTrackingEvent();
						this.initialRun = false;
					});
				}

				this.toggleLoader();
			})
			.catch((e) => {
				console.warn(e);
				this.toggleLoader();
			});
	}

	private async apiSearch(renderSiblings: boolean) {
		return new Promise((resolve) => {
			if (renderSiblings) {
				this.siblingTabs.forEach((sibling) => {
					sibling.search(false);
				});
			}

			this.toggleLoader();
			const lang = apiConfig.apiLanguage;
			const node = apiConfig.apiNode;
			const apiUrl = window.config.search?.apiUrl;

			const url = new URL(`${window.location.origin}/${lang}${apiUrl}${node}`);

			if (this.brandCategoryId && this.brandCategoryId !== 0) {
				this.queryObject.queryData.categoryid = this.brandCategoryId;
				this.queryObject.queryData.isbrandproductsearch = true;
			}

			const params = Object.assign({}, this.queryObject.getParsedQueryObject());

			params.searchtype = getSearchTabNumber(this.tab);

			if (this.queryObject.queryData.keyword) {
				this.queryObject.queryData.keyword = decodeURIComponent(this.queryObject.queryData.keyword);
			}

			fetch(url, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(params),
			})
				.then((response) => {
					if (response.ok) {
						response.json().then((result) => {
							document.querySelectorAll(`[data-tab-count="${this.tab}"]`).forEach(($span) => {
								$span.innerText = `(${result.total})`;
							});
							this.setBrowserQuery();

							this.initializeSearchResults(result);

							this.initialRun = false;
						});
					}

					this.toggleLoader();
				})
				.catch((e) => {
					console.warn(e);
					this.toggleLoader();
				});

			resolve("resolved");
		});
	}

	private setBrowserQuery() {
		this.eventManager.trigger(events.search.SETTING_BROWSER_QUERY);
		const queryString = this.queryObject.getQueryString();
		window.history.replaceState({}, "", `${location.pathname}${queryString}`);
	}

	private parseQuery() {
		return {
			currentPageId: this.currentPageId,
			categoryId: window.config.search?.categoryId,
			page: 1,
			pageSize: 24,
		};
	}

	private toggleLoader() {
		this.dom.toggleLoader();
		this.dom.toggleOverlay();

		this.eventManager.trigger(events.search.TOGGLE_LOADER);
	}

	private initializeSearchResults(result: SearchResult) {
		this.updateResultCount(result.total);

		this.setQueryResultData(result);

		if (result.total === 0) {
			this.showNoResults();
		} else {
			this.showResults();

			if (result.items) {
				this.dom.getResultsContainerElement().innerHTML = "";

				this.renderItems(result.items);
			}

			let showGridListInverted = window.config.search?.showGridListInverted;

			if (
				window.config.search?.apiUrl?.toLowerCase().includes("media") ||
				window.config.search?.apiUrl?.toLowerCase().includes("videomedia")
			) {
				showGridListInverted = false;
			}

			const showGridList = window.config.search?.showGridList?.toLowerCase() === "false" ? false : true;

			const pagingData = {
				keyword: this.queryObject.queryData.keyword,
				query: this.queryObject.queryData.query,
				currentPage: parseInt(this.queryObject.queryData.page),
				pageSize: this.queryObject.queryData.pagesize,
				total: result.total,
				showSummaryLabel: true,
				showGridList: showGridList,
				showGridListInverted: showGridListInverted,
			};

			this.eventManager.trigger(events.search.RENDER, pagingData);

			pagingData.showSummaryLabel = false;

			styleCheckBoxes(this.dom.getCheckBoxElements(true));

			if (result.facets.length > 0) {
				this.dom.addClearFacetsButtonToContainer();

				if (this.initialRun) {
					addListener(this.dom.getClearFacetsContainerElement(), "click", () => {
						this.eventManager.trigger(events.search.FACET_CLEAR_ALL);
					});
				}

				this.updateClearAllBtn();
			} else if (this.dom.getClearFacetsButton()) {
				this.dom.getClearFacetsButton().remove();
			}
		}

		this.eventManager.trigger(events.search.RENDERED, {
			showdiscontinuedproducts: result.showDiscontinuedProducts,
		});
	}

	private renderItems(items) {
		const container = this.dom.getResultsContainerElement();
		items.forEach((item) => {
			container.innerHTML += this.resultItemTemplate(item);
		});
		this.getAdditionalData(items);
	}

	private async getAdditionalData(resultItems) {
		const apiUrl = "/api/contentapi/getcontentresultitem/";
		const bulkSize = 5;
		const itemChunks = chunk(resultItems, bulkSize);

		const tasks = itemChunks.map(async (items) => {
			const params = items.map((item) => ({
				ContentId: item.id,
				ApiLanguage: apiConfig.culture,
				CurrentPageId: apiConfig.apiNode,
				IsBrandSearch: this.queryObject.queryData.isbrandproductsearch,
			}));

			const response = await fetch(apiUrl, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(params),
			});

			if (!response.ok) {
				return;
			}

			const data = await response.json();

			data.forEach((item) => {
				const $container = this.dom.getResultItem(item.id);

				if (item.type === "NotFound") {
					this.dom.hideResultItem(item.id);
				} else {
					this.resultParser.renderResult($container, item);
				}
			});
		});

		await Promise.all(tasks);

		if (this.tab === "products") {
			this.productsComparer.updateCompareStatus();
			this.productsComparer.bindEvents();
		}

		this.searchTracker.bindTrackingEvent();
	}

	private setQueryResultData(result) {
		this.queryObject.data.facetGroups = result.facets;
		this.queryObject.data.selectedFacets = result.selectedFacets;
		this.queryObject.data.totalItems = result.total;
	}

	private setPage(evt) {
		this.queryObject.queryData.page = evt.detail.page;
		this.search();
		const container = this.dom.getFilterPanelElement();
		container.scrollIntoView();
	}

	private updateResultCount(total) {
		this.dom.setTotalResultsText(total, this.tab);
	}

	private updateClearAllBtn() {
		const enable = this.dirtyManager.haveDirtyExtensions();

		this.dom.toggleClearFacetsButton(enable);
	}

	private showResults() {
		this.dom.showElementsHiddenOnNoResults();

		showElement(this.dom.getResultsContainerElement(), "", true);
		showElement(this.dom.getTopPagerElement());
		showElement(this.dom.getBottomPagerElement());

		this.dom.hideNoResults();
	}

	private showNoResults() {
		hideElement(this.dom.getResultsContainerElement());
		hideElement(this.dom.getTopPagerElement());
		hideElement(this.dom.getBottomPagerElement());

		this.dom.showNoResults();
	}

	private toggleFilterPanel() {
		this.dom.addToggleFilterPanelEvents(this.dom.getRefineButtonElement());
		this.dom.addToggleFilterPanelEvents(this.dom.getClosePanelElement());
	}
}
